import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Check, Calendar, Layers, Bot, Smartphone, Cloud, TrendingUp, Loader2 } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const projectTypes = [
  { id: 'saas', label: 'SaaS', icon: Layers, description: 'SaaS product development' },
  { id: 'ai', label: 'AI / Automation', icon: Bot, description: 'AI agents & automation' },
  { id: 'web-mobile', label: 'Web / Mobile', icon: Smartphone, description: 'Apps & platforms' },
  { id: 'cloud', label: 'Cloud', icon: Cloud, description: 'Cloud & DevOps' },
  { id: 'marketing', label: 'Marketing', icon: TrendingUp, description: 'Digital growth' },
];

const budgetRanges = [
  { id: 'under-5k', label: '<$5k', description: 'Small project or MVP' },
  { id: '5k-15k', label: '$5k–$15k', description: 'Standard project' },
  { id: '15k-50k', label: '$15k–$50k', description: 'Complex project' },
  { id: '50k-plus', label: '$50k+', description: 'Enterprise solution' },
];

const timelines = [
  { id: 'asap', label: 'ASAP', description: 'Urgent start needed' },
  { id: '1-month', label: '1 month', description: 'Quick turnaround' },
  { id: '2-3-months', label: '2–3 months', description: 'Standard timeline' },
  { id: '3-plus-months', label: '3+ months', description: 'Flexible timeline' },
];

const steps = [
  { id: 1, title: 'Project Type', subtitle: 'What are you building?' },
  { id: 2, title: 'Budget', subtitle: 'What\'s your investment range?' },
  { id: 3, title: 'Timeline', subtitle: 'When do you need it?' },
  { id: 4, title: 'Your Details', subtitle: 'Let\'s connect' },
];

export const ContactSection = ({ onOpenContactModal }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    projectType: '',
    budget: '',
    timeline: '',
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleOptionSelect = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/inquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) throw new Error('Failed to submit');
      
      setIsSubmitting(false);
      setIsSubmitted(true);
    } catch {
      setIsSubmitting(false);
      alert('Something went wrong. Please try again.');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.projectType !== '';
      case 2:
        return formData.budget !== '';
      case 3:
        return formData.timeline !== '';
      case 4:
        return formData.name !== '' && formData.email !== '';
      default:
        return false;
    }
  };

  const OptionCard = ({ option, isSelected, onClick, showDescription = true }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 ${
        isSelected 
          ? 'border-[#0A3D91] bg-[rgba(10,61,145,0.03)]' 
          : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'
      }`}
      data-testid={`option-${option.id}`}
    >
      <div className="flex items-center gap-3">
        {option.icon && (
          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
              isSelected ? 'bg-[#0A3D91]' : 'bg-[rgba(10,61,145,0.05)]'
            }`}
          >
            <option.icon
              className={`w-5 h-5 ${isSelected ? 'text-white' : 'text-[#0A3D91]'}`}
            />
          </div>
        )}
        <div className="flex-1">
          <p
            className={`font-semibold ${isSelected ? 'text-[#0A3D91]' : 'text-[#131B2E]'}`}
          >
            {option.label}
          </p>
          {showDescription && option.description && (
            <p className="text-sm text-[#475569]">{option.description}</p>
          )}
        </div>
        {isSelected && (
          <div className="w-6 h-6 rounded-full bg-[#0A3D91] flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
      </div>
    </button>
  );

  const sectionRef = useScrollReveal();

  return (
    <section
      id="contact"
      ref={sectionRef}
      data-testid="contact-section"
      className="relative"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      <div className="kv-container">
        <div className="pt-10 pb-12 lg:pt-12 lg:pb-14">
          {/* Section Header */}
          <div className="kv-reveal text-center max-w-2xl mx-auto mb-6">
            <p
              className="text-xs font-semibold uppercase tracking-[0.2em] mb-3"
              style={{ color: '#0A3D91' }}
              data-testid="contact-label"
            >
              Contact
            </p>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: '#131B2E', letterSpacing: '-0.025em' }}
              data-testid="contact-headline"
            >
              Let's Build Something Great
            </h2>
            <p
              className="text-lg"
              style={{ color: '#475569', lineHeight: '1.7' }}
            >
              Share your project details and we'll respond within 24 hours with 
              a clear assessment and next steps.
            </p>
          </div>

          {/* Main Content */}
          <div className="kv-reveal grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Form Section */}
            <div className="lg:col-span-3">
              <div
                className="rounded-xl border p-6"
                style={{ borderColor: '#E2E8F0', boxShadow: 'var(--kv-shadow-sm)' }}
                data-testid="contact-form-container"
              >
                {isSubmitted ? (
                  <div 
                    className="text-center py-12"
                    data-testid="contact-success"
                  >
                    <div
                      className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
                    >
                      <Check className="w-8 h-8 text-[#10B981]" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2" style={{ color: '#131B2E' }}>
                      Thank You!
                    </h3>
                    <p className="text-[#475569] mb-6">
                      We've received your inquiry and will get back to you within 24 hours.
                    </p>
                    <button
                      onClick={() => {
                        setIsSubmitted(false);
                        setCurrentStep(1);
                        setFormData({
                          projectType: '',
                          budget: '',
                          timeline: '',
                          name: '',
                          email: '',
                          company: '',
                          message: '',
                        });
                      }}
                      className="text-[#0A3D91] font-semibold hover:underline"
                    >
                      Submit another inquiry
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Progress Bar */}
                    <div className="mb-8">
                      <div className="flex items-center justify-between mb-2">
                        {steps.map((step, index) => (
                          <div
                            key={step.id}
                            className="flex items-center"
                          >
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                                currentStep >= step.id
                                  ? 'bg-[#0A3D91] text-white'
                                  : 'bg-[#E2E8F0] text-[#475569]'
                              }`}
                            >
                              {currentStep > step.id ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                step.id
                              )}
                            </div>
                            {index < steps.length - 1 && (
                              <div
                                className={`hidden sm:block w-16 lg:w-24 h-1 mx-2 rounded transition-colors ${
                                  currentStep > step.id ? 'bg-[#0A3D91]' : 'bg-[#E2E8F0]'
                                }`}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                      <p className="text-sm text-[#475569]">
                        Step {currentStep} of 4: {steps[currentStep - 1].subtitle}
                      </p>
                    </div>

                    {/* Form Steps */}
                    <form onSubmit={handleSubmit}>
                      {/* Step 1: Project Type */}
                      {currentStep === 1 && (
                        <div className="space-y-3" data-testid="step-project-type">
                          <h3 className="text-xl font-bold mb-4 text-[#131B2E]">
                            What type of project are you planning?
                          </h3>
                          {projectTypes.map((type) => (
                            <OptionCard
                              key={type.id}
                              option={type}
                              isSelected={formData.projectType === type.id}
                              onClick={() => handleOptionSelect('projectType', type.id)}
                            />
                          ))}
                        </div>
                      )}

                      {/* Step 2: Budget */}
                      {currentStep === 2 && (
                        <div className="space-y-3" data-testid="step-budget">
                          <h3 className="text-xl font-bold mb-4 text-[#131B2E]">
                            What's your budget range?
                          </h3>
                          <div className="grid grid-cols-2 gap-3">
                            {budgetRanges.map((range) => (
                              <OptionCard
                                key={range.id}
                                option={range}
                                isSelected={formData.budget === range.id}
                                onClick={() => handleOptionSelect('budget', range.id)}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 3: Timeline */}
                      {currentStep === 3 && (
                        <div className="space-y-3" data-testid="step-timeline">
                          <h3 className="text-xl font-bold mb-4 text-[#131B2E]">
                            When do you need this completed?
                          </h3>
                          <div className="grid grid-cols-2 gap-3">
                            {timelines.map((timeline) => (
                              <OptionCard
                                key={timeline.id}
                                option={timeline}
                                isSelected={formData.timeline === timeline.id}
                                onClick={() => handleOptionSelect('timeline', timeline.id)}
                              />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 4: Contact Details */}
                      {currentStep === 4 && (
                        <div className="space-y-4" data-testid="step-details">
                          <h3 className="text-xl font-bold mb-4 text-[#131B2E]">
                            Tell us about yourself
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-[#131B2E] mb-1.5">
                                Name *
                              </label>
                              <input
                                type="text"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="John Doe"
                                className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] text-[#131B2E] placeholder-[#94A3B8] focus:border-[#00BFFF] focus:ring-2 focus:ring-[#00BFFF]/20 outline-none transition-all"
                                data-testid="input-name"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-[#131B2E] mb-1.5">
                                Email *
                              </label>
                              <input
                                type="email"
                                name="email"
                                required
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="john@company.com"
                                className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] text-[#131B2E] placeholder-[#94A3B8] focus:border-[#00BFFF] focus:ring-2 focus:ring-[#00BFFF]/20 outline-none transition-all"
                                data-testid="input-email"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#131B2E] mb-1.5">
                              Company
                            </label>
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleInputChange}
                              placeholder="Company Inc."
                              className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] text-[#131B2E] placeholder-[#94A3B8] focus:border-[#00BFFF] focus:ring-2 focus:ring-[#00BFFF]/20 outline-none transition-all"
                              data-testid="input-company"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-[#131B2E] mb-1.5">
                              Tell us about your project
                            </label>
                            <textarea
                              name="message"
                              value={formData.message}
                              onChange={handleInputChange}
                              placeholder="Describe your project, goals, and any specific requirements..."
                              rows={4}
                              className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] text-[#131B2E] placeholder-[#94A3B8] focus:border-[#00BFFF] focus:ring-2 focus:ring-[#00BFFF]/20 outline-none transition-all resize-none"
                              data-testid="input-message"
                            />
                          </div>
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div className="flex items-center justify-between mt-6 pt-4 border-t border-[#E2E8F0]">
                        {currentStep > 1 ? (
                          <button
                            type="button"
                            onClick={handleBack}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#475569] hover:text-[#131B2E] transition-colors"
                            data-testid="btn-back"
                          >
                            <ArrowLeft className="w-4 h-4" />
                            Back
                          </button>
                        ) : (
                          <div />
                        )}

                        {currentStep < 4 ? (
                          <button
                            type="button"
                            onClick={handleNext}
                            disabled={!canProceed()}
                            className="kv-btn-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
                            data-testid="btn-next"
                          >
                            Continue
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            type="submit"
                            disabled={!canProceed() || isSubmitting}
                            className="kv-btn-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
                            data-testid="btn-submit"
                          >
                            {isSubmitting ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                Submit Inquiry
                                <ArrowRight className="w-4 h-4" />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {/* Calendly CTA */}
              <div
                className="rounded-xl border p-6 text-center"
                style={{ borderColor: '#E2E8F0', boxShadow: 'var(--kv-shadow-sm)' }}
                data-testid="calendly-cta"
              >
                <div
                  className="w-14 h-14 rounded-xl mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(0, 191, 255, 0.1)' }}
                >
                  <Calendar className="w-7 h-7 text-[#00BFFF]" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-[#131B2E]">
                  Prefer to Talk?
                </h3>
                <p className="text-sm text-[#475569] mb-4">
                  Book a free 30-minute strategy call with our team.
                </p>
                <button
                  onClick={onOpenContactModal}
                  className="kv-btn-outline w-full justify-center"
                  data-testid="calendly-link"
                >
                  <Calendar className="w-4 h-4" />
                  Book Instant Strategy Call
                </button>
              </div>

              {/* Quick Info */}
              <div
                className="rounded-xl border p-6"
                style={{ borderColor: '#E2E8F0', boxShadow: 'var(--kv-shadow-sm)' }}
              >
                <h3 className="text-lg font-bold mb-4 text-[#131B2E]">
                  What Happens Next?
                </h3>
                <div className="space-y-4">
                  {[
                    { step: '1', text: 'We review your project details' },
                    { step: '2', text: 'Our team reaches out within 24 hours' },
                    { step: '3', text: 'We schedule a discovery call' },
                    { step: '4', text: 'You receive a custom proposal' },
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-3">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                        style={{ backgroundColor: 'rgba(10, 61, 145, 0.1)', color: '#0A3D91' }}
                      >
                        {item.step}
                      </div>
                      <p className="text-sm text-[#475569]">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Info */}
              <div
                className="rounded-xl border p-6"
                style={{ borderColor: '#E2E8F0', boxShadow: 'var(--kv-shadow-sm)' }}
              >
                <h3 className="text-lg font-bold mb-4 text-[#131B2E]">
                  Direct Contact
                </h3>
                <div className="space-y-3">
                  <a
                    href="mailto:hello@kritvasoft.com"
                    className="block text-sm text-[#475569] hover:text-[#0A3D91] transition-colors"
                  >
                    hello@kritvasoft.com
                  </a>
                  <p className="text-sm text-[#475569]">
                    Response time: Within 24 hours
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
